import { AppAssets } from '../contexts/AuthContext';

// Store the created object URL to avoid revoking it and creating duplicates.
let manifestUrl: string | null = null;

/**
 * Dynamically updates the PWA manifest and apple-touch-icon links in the document's <head>.
 * This allows for live branding updates without requiring a service worker update.
 * @param assets The application assets containing the base64 icon data.
 */
export const updatePwaAssets = async (assets: AppAssets): Promise<void> => {
    // 1. Update apple-touch-icon
    // This is the icon used when a user adds the site to their homescreen on iOS.
    const appleTouchIconLink = document.getElementById('apple-touch-icon-link') as HTMLLinkElement;
    if (appleTouchIconLink && assets.appleTouchIcon && appleTouchIconLink.href !== assets.appleTouchIcon) {
        appleTouchIconLink.href = assets.appleTouchIcon;
    }

    // 2. Update the manifest.json link
    // The manifest controls the PWA's appearance, including icons on Android and desktop.
    const manifestLink = document.getElementById('manifest-link') as HTMLLinkElement;
    if (manifestLink && assets.pwaIcon192 && assets.pwaIcon512) {
        try {
            // Fetch the base manifest to use as a template. This is better than building
            // from scratch as it preserves other manifest properties.
            const response = await fetch('/manifest.json');
            if (!response.ok) {
                console.error("Could not fetch base manifest.json to update icons.");
                return;
            }
            const manifest = await response.json();

            // Replace the static icon list with the dynamic icons from assets.
            // Using the same image for 'any' and 'maskable' is fine, as long as
            // the uploaded image is designed to work well with masking.
            manifest.icons = [
                { src: assets.pwaIcon192, type: 'image/png', sizes: '192x192', purpose: 'any maskable' },
                { src: assets.pwaIcon512, type: 'image/png', sizes: '512x512', purpose: 'any maskable' },
            ];
            
            // Also include the apple-touch-icon in the manifest for completeness.
            // We declare it as 180x180, its actual size from the wizard.
            if (assets.appleTouchIcon) {
                manifest.icons.push({ src: assets.appleTouchIcon, type: 'image/png', sizes: '180x180', purpose: 'any' });
            }

            const manifestString = JSON.stringify(manifest);
            const blob = new Blob([manifestString], { type: 'application/json' });

            // To apply the new manifest, we create a temporary URL for the blob.
            const newManifestUrl = URL.createObjectURL(blob);

            // Revoke the old URL to prevent memory leaks if we've created one before.
            if (manifestUrl) {
                URL.revokeObjectURL(manifestUrl);
            }

            // Store the new URL and apply it to the <link> tag.
            manifestUrl = newManifestUrl;
            manifestLink.href = manifestUrl;

        } catch (error) {
            console.error("Failed to dynamically update manifest:", error);
        }
    }
};
